import { useState } from 'react';

import { cn } from '../../lib/cn.js';
import { ChartTip, tetoRedondo, type TipRow } from './shared.js';

export type ColumnTone = 'neutral' | 'soft' | 'danger';

export interface ColumnItem {
  readonly key: string;
  /** Rótulo do eixo. Curto: cabe numa coluna de 24px com folga. */
  readonly label: string;
  readonly value: number;
  readonly tip: { readonly title: string; readonly rows: readonly TipRow[] };
  /** O que o leitor de tela anuncia ao focar a coluna. */
  readonly aria: string;
  readonly tone?: ColumnTone | undefined;
}

/**
 * Preenchimento por tom, em repouso e sob o ponteiro.
 *
 * Neutro é a regra: a série é uma só e o título já diz o que está plotado.
 * `soft` é o período ainda em curso — um mês que não acabou não se compara
 * com os fechados. `danger` é reservado a uma categoria que **é** um estado
 * ruim, como contratos já vencidos; nunca para distinguir séries.
 */
const FILL: Record<ColumnTone, readonly [string, string]> = {
  neutral: ['bg-foreground/75', 'bg-foreground'],
  soft: ['bg-foreground/35', 'bg-foreground/55'],
  danger: ['bg-danger/80', 'bg-danger'],
};

/**
 * Colunas de uma série só.
 *
 * Marca fina (até 24px), ponta de dado arredondada e base reta, três linhas
 * de referência em filete e a dica na superfície invertida. O `pt-12` acima
 * das colunas é o respiro da dica: sem ele, a da coluna mais alta sai do
 * cartão.
 */
export function Columns({
  items,
  format,
  integer = false,
  height = 160,
}: {
  items: readonly ColumnItem[];
  /** Como escrever o valor nas linhas de referência. */
  format: (value: number) => string;
  /** Contagem: teto par, linha do meio inteira. */
  integer?: boolean | undefined;
  height?: number | undefined;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  const max = items.reduce((maior, item) => Math.max(maior, item.value), 0);
  const teto = tetoRedondo(max, integer);
  const thinLabels = items.length > 8;

  return (
    <div className="relative pt-12">
      <div className="pointer-events-none absolute inset-x-0 top-12" style={{ height }}>
        {[0, 0.5, 1].map((fracao) => (
          <div
            key={fracao}
            className="absolute inset-x-0 flex items-center gap-2"
            style={{ top: `${(1 - fracao) * 100}%` }}
          >
            <span className="tabular w-14 shrink-0 text-right text-[11px] text-muted-foreground">
              {format(teto * fracao)}
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
        ))}
      </div>

      <div className="flex items-end gap-0.5 pl-16" style={{ height }}>
        {items.map((item, index) => {
          const altura = teto > 0 ? (item.value / teto) * 100 : 0;
          const ativo = hovered === item.key;
          const fill = FILL[item.tone ?? 'neutral'];
          const align = index < 2 ? 'start' : index > items.length - 3 ? 'end' : 'center';

          return (
            <div
              key={item.key}
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(item.key)}
              onBlur={() => setHovered(null)}
              tabIndex={0}
              role="img"
              aria-label={item.aria}
              className="relative flex h-full flex-1 cursor-default items-end justify-center rounded-sm"
            >
              <span
                className={cn(
                  'w-full max-w-6 rounded-t-[4px] transition-colors',
                  ativo ? fill[1] : fill[0],
                )}
                style={{ height: `${Math.max(altura, item.value > 0 ? 2 : 0)}%` }}
              />

              {ativo ? (
                <ChartTip title={item.tip.title} rows={item.tip.rows} align={align} />
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex gap-0.5 pl-16">
        {items.map((item, index) => (
          <span
            key={item.key}
            className={cn(
              'tabular flex-1 text-center text-[11px] leading-tight text-muted-foreground',
              item.tone === 'danger' && 'font-medium text-danger',
            )}
          >
            {/* Com muitas colunas, rotula uma sim outra não para não colidir. */}
            {thinLabels && index % 2 === 1 ? '' : item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
